/**
 * La feuille PUBLIÉE (moteur v2, `track = 'packs'`) d'une question ISOLÉE — Warmup, automatismes, diagnostic : ces
 * écrans servent des questions une à une et ne jouaient que la « feuille pour calculer » (libre), même quand la
 * question a sa vraie feuille. RPC `get_question_scratch_models` (migration 0703, mêmes droits que le Warmup).
 *
 * Deux sessions reliées, comme dans le player d'exercice : l'interface du moteur v2 (`V2Session`) et la session du
 * player (`ScratchSession`, moteur v2 aussi) qui rejoue chaque ligne gardée (`acceptText`) et sait quelles
 * propositions du QCM le brouillon établit (✓ sur la touche) ou montre fausses. Ici, AUCUN HORA (ces écrans n'ont
 * pas l'économie de la feuille d'un exercice) et rien en base : les lignes restent sur l'appareil.
 *
 *   undefined : on ne sait pas encore (la question se charge) ;
 *   null      : pas de feuille publiée (ou moteur indisponible) → la feuille libre.
 */
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { V2Session, V2StoredLine } from '@/features/brouillon-v2';
import { ScratchSession } from './scratch-session';
import { loadScratchData, type ScratchData, type V2Ui } from './scratch-data';
import { withChoiceValues } from './choice-values';

export interface QuestionSheet {
  ui: V2Ui;
  session: V2Session;
  /** la session du player : propositions établies (✓) / montrées fausses (✗) */
  base: ScratchSession;
}

type UntypedRpc = (
  name: string,
  params: Record<string, unknown>,
) => Promise<{ data: unknown; error: { message?: string } | null }>;

/** Les feuilles publiées des questions (au plus 30) ; null : erreur ou réponse refusée. */
export async function fetchQuestionSheets(questionIds: string[]): Promise<ScratchData | null> {
  const { data, error } = await (supabase as unknown as { rpc: UntypedRpc }).rpc(
    'get_question_scratch_models',
    { p_question_ids: questionIds },
  );
  if (error) throw new Error(error.message ?? 'get_question_scratch_models');
  const payload = (data ?? {}) as { models?: unknown; lines?: unknown; error?: unknown };
  if (payload.error) return null;
  return loadScratchData(payload);
}

/** Lignes de la feuille publiée gardées sur l'appareil (texte et sorte, pour les rejouer). */
function pubKey(key: string | null): string | null {
  return key ? `${key}:pub` : null;
}
export function loadPublishedLines(key: string | null): V2StoredLine[] {
  const k = pubKey(key);
  if (!k) return [];
  try {
    const raw = JSON.parse(localStorage.getItem(k) ?? '[]') as unknown;
    if (!Array.isArray(raw)) return [];
    return raw.flatMap((l) => {
      const { text, kind } = l as { text?: unknown; kind?: unknown };
      return typeof text === 'string' && text && (kind === 'step' || kind === 'kept')
        ? [{ text, kind, hora: 0, hinted: false }]
        : [];
    });
  } catch {
    return [];
  }
}
function savePublishedLines(key: string | null, session: V2Session): void {
  const k = pubKey(key);
  if (!k) return;
  try {
    localStorage.setItem(
      k,
      JSON.stringify(
        session.lines
          .filter((l) => !l.stopped && !l.aside)
          .slice(0, 60)
          .map((l) => ({ text: l.text, kind: l.verdict.kind === 'kept' ? 'kept' : 'step' })),
      ),
    );
  } catch {
    /* stockage indisponible : la feuille vit le temps de la page */
  }
}

export function useQuestionSheet({
  questionKey,
  storageKey,
  locked,
  enabled = true,
  statement,
}: {
  /** id du bloc question (`exercise_blocks`) */
  questionKey: string;
  /** énoncé de la question : ses formules et ses nombres au clavier */
  statement?: { promptMd: string; level?: string | null };
  storageKey: string | null;
  locked: boolean;
  enabled?: boolean;
}): QuestionSheet | null | undefined {
  const q = useQuery({
    queryKey: ['question-sheet', questionKey],
    queryFn: () => fetchQuestionSheets([questionKey]),
    enabled: enabled && !!questionKey,
    staleTime: Infinity,
    retry: 1,
  });

  const sheet = useMemo<QuestionSheet | null | undefined>(() => {
    if (!enabled) return null;
    if (q.isLoading) return undefined;
    const data = q.data;
    const entry = data?.models.get(questionKey);
    if (!data || !entry || entry.track !== 'packs' || !data.packs || !data.v2) return null;
    try {
      const base = new ScratchSession({
        model: withChoiceValues(entry.model),
        maxHora: entry.maxHora,
        packs: data.packs,
        level: entry.level,
        stepDelayMs: 0,
      });
      const ui = data.v2;
      const session: V2Session = new ui.V2Session({
        model: entry.model,
        moreKeys: !!entry.moreKeys,
        ...(statement ? { statement } : {}),
        // chaque ligne gardée est rejouée par la session du player (✓ du QCM), sans HORA
        onCommit: (line) => {
          base.acceptText(line.text, line.hinted, true);
          savePublishedLines(storageKey, session);
          return 0;
        },
      });
      const stored = loadPublishedLines(storageKey);
      if (stored.length) {
        session.replay(stored);
        for (const l of session.lines) base.acceptText(l.text, false, true);
        for (const l of session.lines) l.gain = 0;
      }
      return { ui, session, base };
    } catch (e) {
      console.warn('[scratch] feuille publiée ignorée', questionKey, e);
      return null;
    }
    // une feuille par question : la clé de stockage en découle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, q.isLoading, q.data, questionKey]);

  useEffect(() => {
    if (!locked || !sheet) return;
    if (!sheet.session.locked) sheet.session.lock();
    sheet.base.lock();
  }, [locked, sheet]);
  useEffect(() => () => sheet?.base.dispose(), [sheet]);

  return sheet;
}
